import { useState, useEffect, useMemo } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/App';
import { Key, Check, AlertCircle, Search, Save, Upload, RefreshCw, Trash2, ExternalLink } from 'lucide-react';

/**
 * Page d'administration : saisie manuelle des local_key Tuya.
 * Contourne complètement l'API Cloud (utile quand le quota Trial est épuisé).
 * L'utilisateur récupère chaque local_key via iot.tuya.com → Devices → Debug Device.
 */
const TuyaLocalKeys = () => {
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({ total: 0, with_local_key: 0, missing_local_key: 0, with_ip: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('missing'); // all | missing | done
  const [drafts, setDrafts] = useState({}); // { device_id: local_key }
  const [rowStatus, setRowStatus] = useState({}); // { device_id: 'saving' | 'saved' | 'error' }
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkResult, setBulkResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/integrations/tuya/local-keys/devices');
      if (data.success) {
        setDevices(data.devices);
        setStats({
          total: data.total,
          with_local_key: data.with_local_key,
          missing_local_key: data.missing_local_key,
          with_ip: data.with_ip,
        });
      }
    } catch (e) {
      console.error('Load error', e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return devices.filter(d => {
      if (filter === 'missing' && d.local_key) return false;
      if (filter === 'done' && !d.local_key) return false;
      if (!q) return true;
      const name = (d.name || d.tuya_name || '').toLowerCase();
      return name.includes(q) || d.device_id.toLowerCase().includes(q) || (d.product_name || '').toLowerCase().includes(q);
    });
  }, [devices, search, filter]);

  const saveOne = async (deviceId) => {
    const key = (drafts[deviceId] || '').trim();
    if (key.length < 8) {
      setRowStatus(s => ({ ...s, [deviceId]: 'error' }));
      return;
    }
    setRowStatus(s => ({ ...s, [deviceId]: 'saving' }));
    try {
      const { data } = await api.post('/integrations/tuya/local-keys/set', {
        device_id: deviceId,
        local_key: key,
      });
      if (data.success) {
        setRowStatus(s => ({ ...s, [deviceId]: 'saved' }));
        setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, local_key: key } : d));
        setStats(s => ({ ...s, with_local_key: s.with_local_key + 1, missing_local_key: Math.max(0, s.missing_local_key - 1) }));
        setDrafts(d => { const n = { ...d }; delete n[deviceId]; return n; });
        setTimeout(() => setRowStatus(s => { const n = { ...s }; delete n[deviceId]; return n; }), 1500);
      } else {
        setRowStatus(s => ({ ...s, [deviceId]: 'error' }));
      }
    } catch (e) {
      setRowStatus(s => ({ ...s, [deviceId]: 'error' }));
    }
  };

  const clearOne = async (deviceId) => {
    if (!window.confirm('Supprimer la local_key de cet appareil ?')) return;
    try {
      await api.delete(`/integrations/tuya/local-keys/${deviceId}`);
      setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, local_key: null } : d));
      setStats(s => ({ ...s, with_local_key: Math.max(0, s.with_local_key - 1), missing_local_key: s.missing_local_key + 1 }));
    } catch (e) {
      console.error(e);
    }
  };

  const parseBulk = (text) => {
    // Accepte:
    //  - JSON array: [{device_id, local_key}, ...]
    //  - JSON object: {device_id: local_key, ...}
    //  - CSV/TSV: device_id,local_key  (une ligne par appareil)
    text = text.trim();
    if (!text) return [];
    // JSON
    if (text.startsWith('[') || text.startsWith('{')) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) return parsed;
        return Object.entries(parsed).map(([device_id, local_key]) => ({ device_id, local_key }));
      } catch (e) { /* fall through */ }
    }
    // Lignes CSV/TSV
    const entries = [];
    text.split(/\r?\n/).forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const parts = trimmed.split(/[,\t;|]+/).map(p => p.trim()).filter(Boolean);
      if (parts.length >= 2) {
        // Détecter lequel est le device_id (chaîne alphanumérique ~22 chars)
        const [a, b, c] = parts;
        let device_id = a, local_key = b, ip = c;
        if (b && /^[a-z0-9]{18,}$/i.test(b) && !/^[a-z0-9]{18,}$/i.test(a)) {
          device_id = b; local_key = a;
        }
        entries.push({ device_id, local_key, ...(ip ? { ip } : {}) });
      }
    });
    return entries;
  };

  const submitBulk = async () => {
    const entries = parseBulk(bulkText);
    if (!entries.length) {
      setBulkResult({ success: false, error: 'Aucune entrée valide détectée.' });
      return;
    }
    setBulkResult({ loading: true });
    try {
      const { data } = await api.post('/integrations/tuya/local-keys/bulk-set', { entries });
      setBulkResult(data);
      await load();
    } catch (e) {
      setBulkResult({ success: false, error: String(e) });
    }
  };

  const reloadService = async () => {
    try {
      const { data } = await api.post('/integrations/tuya/local-keys/load-into-service');
      alert(`Service rechargé : ${data.loaded} local_keys actives`);
    } catch (e) {
      alert('Erreur : ' + e);
    }
  };

  const progress = stats.total > 0 ? (stats.with_local_key / stats.total) * 100 : 0;

  return (
    <Layout>
      <div className="p-6 max-w-[1400px] mx-auto text-white" data-testid="tuya-local-keys-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Key className="w-8 h-8 text-amber-400" />
            Tuya Local Keys — Saisie manuelle
          </h1>
          <p className="text-slate-400 mt-2 text-sm">
            Contournement du quota Cloud Tuya. Récupérez chaque <code className="bg-slate-800 px-1 rounded">local_key</code> via{' '}
            <a href="https://iot.tuya.com" target="_blank" rel="noreferrer" className="text-cyan-400 inline-flex items-center gap-1 hover:underline">
              iot.tuya.com <ExternalLink className="w-3 h-3" />
            </a>
            {' '}→ Cloud → Development → votre projet → <b>Devices</b> → <b>Debug Device</b> → copiez <b>Device Local Key</b>.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-4" data-testid="stat-total">
            <div className="text-xs text-slate-400 uppercase">Total appareils</div>
            <div className="text-3xl font-bold mt-1">{stats.total}</div>
          </div>
          <div className="bg-emerald-950 border border-emerald-700 rounded-lg p-4" data-testid="stat-with-key">
            <div className="text-xs text-emerald-300 uppercase">Avec local_key</div>
            <div className="text-3xl font-bold mt-1 text-emerald-300">{stats.with_local_key}</div>
          </div>
          <div className="bg-amber-950 border border-amber-700 rounded-lg p-4" data-testid="stat-missing">
            <div className="text-xs text-amber-300 uppercase">À renseigner</div>
            <div className="text-3xl font-bold mt-1 text-amber-300">{stats.missing_local_key}</div>
          </div>
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
            <div className="text-xs text-slate-400 uppercase">Avec IP locale</div>
            <div className="text-3xl font-bold mt-1">{stats.with_ip}</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-all"
              style={{ width: `${progress}%` }}
              data-testid="progress-bar"
            />
          </div>
          <div className="text-xs text-slate-400 mt-1">{progress.toFixed(0)}% complété</div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher par nom, ID, produit..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-1 bg-slate-900 border border-slate-700 rounded p-1">
            {[
              { k: 'missing', l: 'À faire' },
              { k: 'done', l: 'Faits' },
              { k: 'all', l: 'Tous' },
            ].map(f => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={`px-3 py-1 text-xs rounded ${filter === f.k ? 'bg-cyan-600' : 'hover:bg-slate-800'}`}
                data-testid={`filter-${f.k}`}
              >{f.l}</button>
            ))}
          </div>
          <button
            onClick={() => setBulkOpen(v => !v)}
            className="px-3 py-2 bg-purple-700 hover:bg-purple-600 rounded text-sm flex items-center gap-2"
            data-testid="toggle-bulk-import"
          >
            <Upload className="w-4 h-4" /> Import en masse
          </button>
          <button
            onClick={reloadService}
            className="px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded text-sm flex items-center gap-2"
            data-testid="reload-service-btn"
            title="Recharger les clés en mémoire dans le service Tuya"
          >
            <RefreshCw className="w-4 h-4" /> Activer
          </button>
        </div>

        {/* Bulk import panel */}
        {bulkOpen && (
          <div className="mb-6 bg-slate-900 border border-purple-700 rounded-lg p-4" data-testid="bulk-import-panel">
            <h3 className="font-bold mb-2 flex items-center gap-2"><Upload className="w-4 h-4" /> Import en masse</h3>
            <p className="text-xs text-slate-400 mb-2">
              Collez au choix :<br/>
              • JSON array : <code className="bg-slate-800 px-1">[{'{'}"device_id":"bfc7c...","local_key":"aBcD..."{'}'}]</code><br/>
              • JSON object : <code className="bg-slate-800 px-1">{'{'}"bfc7c...": "aBcD...", "..." : "..."{'}'}</code><br/>
              • Lignes CSV : <code className="bg-slate-800 px-1">device_id,local_key</code> (une ligne par appareil)
            </p>
            <textarea
              value={bulkText}
              onChange={e => setBulkText(e.target.value)}
              rows={8}
              placeholder='bfc7c36c4630e94624mkbz,aBcD1234EfGh5678'
              className="w-full p-3 bg-slate-950 border border-slate-700 rounded font-mono text-xs"
              data-testid="bulk-import-textarea"
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={submitBulk}
                className="px-4 py-2 bg-emerald-700 hover:bg-emerald-600 rounded text-sm font-bold"
                data-testid="bulk-submit-btn"
              >Importer</button>
              <button
                onClick={() => { setBulkText(''); setBulkResult(null); }}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-sm"
              >Effacer</button>
            </div>
            {bulkResult && (
              <div className={`mt-3 p-3 rounded text-sm ${bulkResult.success ? 'bg-emerald-950 border border-emerald-700' : 'bg-red-950 border border-red-700'}`}>
                {bulkResult.loading ? 'Import en cours...' : bulkResult.success ? (
                  <>✅ {bulkResult.updated} appareils mis à jour sur {bulkResult.total_submitted}.
                    {bulkResult.errors?.length > 0 && <div className="mt-1 text-xs text-amber-300">Erreurs : {bulkResult.errors.length}</div>}
                  </>
                ) : (<>❌ {bulkResult.error}</>)}
              </div>
            )}
          </div>
        )}

        {/* Device list */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Chargement...</div>
        ) : (
          <div className="bg-slate-900 border border-slate-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-800">
                <tr>
                  <th className="text-left p-3">Nom</th>
                  <th className="text-left p-3">Device ID</th>
                  <th className="text-left p-3">Produit</th>
                  <th className="text-left p-3">IP locale</th>
                  <th className="text-left p-3 w-[350px]">Local Key</th>
                  <th className="text-left p-3 w-[110px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-500">Aucun appareil ne correspond.</td></tr>
                ) : filtered.map(d => {
                  const hasKey = !!d.local_key;
                  const st = rowStatus[d.device_id];
                  const draft = drafts[d.device_id] || '';
                  return (
                    <tr key={d.device_id} className={`border-t border-slate-800 hover:bg-slate-800/50 ${hasKey ? 'opacity-70' : ''}`} data-testid={`row-${d.device_id}`}>
                      <td className="p-3 font-medium">{d.name || d.tuya_name || '—'}</td>
                      <td className="p-3 font-mono text-xs text-slate-400">{d.device_id}</td>
                      <td className="p-3 text-xs text-slate-400">{d.product_name || d.category || '—'}</td>
                      <td className="p-3 text-xs text-slate-400">{d.ip || '—'}</td>
                      <td className="p-3">
                        {hasKey ? (
                          <div className="flex items-center gap-2 text-emerald-400">
                            <Check className="w-4 h-4" />
                            <span className="font-mono text-xs">{d.local_key.slice(0, 8)}...{d.local_key.slice(-4)}</span>
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={draft}
                            onChange={e => setDrafts(dr => ({ ...dr, [d.device_id]: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') saveOne(d.device_id); }}
                            placeholder="Coller la local_key..."
                            className="w-full px-2 py-1 bg-slate-950 border border-slate-700 rounded font-mono text-xs"
                            data-testid={`input-${d.device_id}`}
                          />
                        )}
                      </td>
                      <td className="p-3">
                        {hasKey ? (
                          <button
                            onClick={() => clearOne(d.device_id)}
                            className="p-1.5 bg-red-900/50 hover:bg-red-800 rounded text-red-300"
                            title="Supprimer"
                            data-testid={`clear-${d.device_id}`}
                          ><Trash2 className="w-4 h-4" /></button>
                        ) : (
                          <button
                            onClick={() => saveOne(d.device_id)}
                            disabled={!draft || st === 'saving'}
                            className={`px-3 py-1.5 rounded text-xs flex items-center gap-1 ${
                              st === 'saved' ? 'bg-emerald-600' :
                              st === 'error' ? 'bg-red-600' :
                              'bg-cyan-700 hover:bg-cyan-600 disabled:bg-slate-700 disabled:cursor-not-allowed'
                            }`}
                            data-testid={`save-${d.device_id}`}
                          >
                            {st === 'saving' ? '...' :
                             st === 'saved' ? <><Check className="w-3 h-3" /> OK</> :
                             st === 'error' ? <><AlertCircle className="w-3 h-3" /> Err</> :
                             <><Save className="w-3 h-3" /> Save</>}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 text-xs text-slate-500">
          💡 Astuce : tapez quelques caractères dans « Rechercher » pour filtrer par pièce (ex. « salon », « cuisine »).
          Utilisez <b>Entrée</b> pour sauvegarder rapidement. L'onglet <b>Import en masse</b> permet de coller toutes les clés d'un coup si vous les avez en JSON/CSV.
        </div>
      </div>
    </Layout>
  );
};

export default TuyaLocalKeys;
